import axios, { AxiosInstance } from 'axios';

// Use relative URL by default to leverage the CRA proxy, fallback to env variable
const API_URL = process.env.REACT_APP_API_URL || '/api';

const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  timeout: 60000 // Increased to 60 seconds for file uploads
});

// In-Memory GET Request TTL Cache
interface CacheEntry {
  data: any;
  timestamp: number;
}
const apiCache = new Map<string, CacheEntry>();
const CACHE_TTL_MS = 30000; // 30 seconds

// Request interceptor to add JWT token & check/invalidate TTL cache
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const method = config.method?.toLowerCase();
    // Invalidate cache on mutations (POST, PUT, DELETE, PATCH)
    if (method && ['post', 'put', 'delete', 'patch'].includes(method)) {
      apiCache.clear();
    }

    // Check TTL Cache for GET requests
    if (method === 'get' && !(config as any).skipCache) {
      const cacheKey = `${config.baseURL || ''}${config.url}`;
      const cached = apiCache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp < CACHE_TTL_MS)) {
        config.adapter = () => Promise.resolve({
          data: cached.data,
          status: 200,
          statusText: 'OK (Cached)',
          headers: config.headers,
          config: config
        });
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling & caching
api.interceptors.response.use(
  (response) => {
    // Validate response structure
    if (!response.data) {
      console.error('[API] Response missing data:', response);
      return Promise.reject(new Error('Invalid response from server: empty data'));
    }

    // Cache successful GET responses
    if (response.config.method?.toLowerCase() === 'get' && !(response.config as any).skipCache) {
      const cacheKey = `${response.config.baseURL || ''}${response.config.url}`;
      apiCache.set(cacheKey, {
        data: response.data,
        timestamp: Date.now()
      });
    }

    return response;
  },
  (error) => {
    // Log detailed error info for debugging
    console.error('[API] Request failed:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
      config: {
        url: error.config?.url,
        method: error.config?.method,
        baseURL: error.config?.baseURL
      }
    });

    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
      // Clear authentication data
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');

      // Clear MSAL cache to prevent token reuse
      const msalKeys = Object.keys(localStorage).filter(
        key => key.includes('msal') || key.includes('microsoft')
      );
      msalKeys.forEach(key => localStorage.removeItem(key));

      // Clear session storage
      sessionStorage.clear();

      // Use window.location.replace for cleaner redirect without history
      window.location.replace('/login');
    }

    // Provide more helpful error messages
    if (!error.response) {
      console.error('[API] Network error - unable to reach server:', {
        message: error.message,
        code: error.code,
        baseURL: error.config?.baseURL,
        url: error.config?.url
      });
      return Promise.reject(new Error(`Network error: ${error.message}. Please check if the backend server is running.`));
    }

    return Promise.reject(error);
  }
);

// Auth API calls
export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),

  microsoftLogin: (data: { accessToken: string }) =>
    api.post('/auth/microsoft-login', data),

  microsoftCallback: (code: string) =>
    api.post('/auth/microsoft-callback', { code }),

  getCurrentUser: () =>
    api.get('/auth/me'),

  logout: () =>
    api.post('/auth/logout'),

  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),

  resetPassword: (token: string, email: string, newPassword: string) =>
    api.post('/auth/reset-password', { token, email, newPassword })
};

// Leave API calls
export const leaveAPI = {
  getAll: () =>
    api.get('/leaves'),

  getUserLeaves: (userId: string) =>
    api.get(`/leaves/user/${userId}`),

  getById: (id: string) =>
    api.get(`/leaves/${id}`),

  create: (leaveData: any) =>
    api.post('/leaves', leaveData),

  update: (id: string, leaveData: any) =>
    api.put(`/leaves/${id}`, leaveData),

  updateAttachments: (id: string, attachments: any[]) =>
    api.put(`/leaves/${id}/attachments`, { attachments }),

  delete: (id: string) =>
    api.delete(`/leaves/${id}`)
};

// Asset API calls
export const assetAPI = {
  getAll: () =>
    api.get('/assets'),

  getAssetCode: () =>
    api.get('/assets/asset-code'),

  getById: (id: string) =>
    api.get(`/assets/${id}`),

  getByUser: (userId: string) =>
    api.get(`/assets/user/${userId}`),

  create: (assetData: any) =>
    api.post('/assets', assetData),

  assign: (id: string, assignmentData: any) =>
    api.post(`/assets/${id}/assign`, assignmentData),

  return: (id: string) =>
    api.post(`/assets/${id}/return`),

  initiateReturn: (id: string) =>
    api.post(`/assets/${id}/initiate-return`),

  completeReturn: (id: string) =>
    api.post(`/assets/${id}/complete-return`),

  update: (id: string, assetData: any) =>
    api.put(`/assets/${id}`, assetData),

  delete: (id: string) =>
    api.delete(`/assets/${id}`)
};

// HR API calls
export const hrAPI = {
  getAll: () =>
    api.get('/hr'),

  getNextId: () =>
    api.get('/hr/next-id'),

  getAllRecords: () =>
    api.get('/hr/records'),

  getEmployeeProfile: (empId: string) =>
    api.get(`/hr/employee/${empId}`),

  getById: (id: string) =>
    api.get(`/hr/${id}`),

  getAllUsers: () =>
    api.get('/hr/users/all'),

  create: (userData: any) =>
    api.post('/hr/create-user', userData),

  createHRRecord: (hrData: any) =>
    api.post('/hr', hrData),

  update: (id: string, hrData: any) =>
    api.put(`/hr/${id}`, hrData),

  updateUser: (id: string, userData: any) =>
    api.put(`/hr/user/${id}`, userData),

  updateUserManager: (userId: string, managerId: string) =>
    api.put(`/hr/user/${userId}/manager`, { managerId }),

  delete: (id: string) =>
    api.delete(`/hr/${id}`)
};

// Notification API calls
export const notificationAPI = {
  getPendingCount: () =>
    api.get('/notifications/pending-count'),

  getPending: () =>
    api.get('/notifications/pending')
};

// Expense API calls
export const expenseAPI = {
  getAll: () =>
    api.get('/expenses'),

  getById: (id: string) =>
    api.get(`/expenses/${id}`),

  create: (expenseData: any) =>
    api.post('/expenses', expenseData),

  update: (id: string, expenseData: any) =>
    api.put(`/expenses/${id}`, expenseData),

  updateReceipts: (id: string, receipts: any[]) =>
    api.put(`/expenses/${id}/receipts`, { receipts }),

  delete: (id: string) =>
    api.delete(`/expenses/${id}`)
};

// Form API calls
export const formAPI = {
  getAll: () =>
    api.get('/forms'),

  getById: (id: string) =>
    api.get(`/forms/${id}`),

  create: (formData: any) =>
    api.post('/forms', formData),

  update: (id: string, formData: any) =>
    api.put(`/forms/${id}`, formData),

  review: (id: string, reviewData: any) =>
    api.post(`/forms/${id}/review`, reviewData),

  delete: (id: string) =>
    api.delete(`/forms/${id}`),

  sendEmail: (emailData: { to: string; subject: string; body: string; formId: string }) =>
    api.post('/forms/send-email', emailData),

  sendToInbox: (inboxData: { userId: string; formId: string; formTitle: string; message: string; type: string }) =>
    api.post('/forms/send-to-inbox', inboxData),

  submitResponse: (responseData: { formId: string; formName: string; formData: any; answers: any }) =>
    api.post('/forms/submit-response', responseData),

  getResponses: (formName: string) =>
    api.get(`/forms/responses/${formName}`),

  getAllResponses: () =>
    api.get('/forms/responses')
};

// Holiday API calls
export const holidayAPI = {
  getAll: () =>
    api.get('/holidays'),

  getByDateRange: (fromDate: number, toDate: number) =>
    api.get(`/holidays/range/${fromDate}/${toDate}`),

  create: (holidayData: any) =>
    api.post('/holidays', holidayData),

  update: (id: string, holidayData: any) =>
    api.put(`/holidays/${id}`, holidayData),

  delete: (id: string) =>
    api.delete(`/holidays/${id}`)
};

// Ticket API calls
export const ticketAPI = {
  getAll: () =>
    api.get('/tickets'),

  getById: (id: string) =>
    api.get(`/tickets/${id}`),

  create: (ticketData: any) =>
    api.post('/tickets', ticketData),

  update: (id: string, ticketData: any) =>
    api.put(`/tickets/${id}`, ticketData),

  delete: (id: string) =>
    api.delete(`/tickets/${id}`)
};

// Resource API calls (for contractors and external resources)
export const resourceAPI = {
  getAll: (params?: any) =>
    api.get('/resources', { params }),

  getById: (id: string) =>
    api.get(`/resources/${id}`),

  getAllSkills: () =>
    api.get('/resources/skills/all'),

  create: (resourceData: any) =>
    api.post('/resources', resourceData),

  update: (id: string, resourceData: any) =>
    api.put(`/resources/${id}`, resourceData),

  addProject: (id: string, projectData: any) =>
    api.post(`/resources/${id}/projects`, projectData),

  updateAvailability: (id: string, availabilityData: any) =>
    api.patch(`/resources/${id}/availability`, availabilityData),

  addDocument: (id: string, documentData: any) =>
    api.post(`/resources/${id}/documents`, documentData),

  deleteDocument: (id: string, docId: string) =>
    api.delete(`/resources/${id}/documents/${docId}`),

  delete: (id: string) =>
    api.delete(`/resources/${id}`)
};

export const fileAPI = {
  uploadUserDocument: (data: { fileData: string; fileName: string; employeeNo: string }) =>
    api.post('/files/user-document', data),

  uploadProfilePicture: (data: { fileData: string; fileName: string; employeeNo: string }) =>
    api.post('/files/profile-picture', data),

  uploadIdentityProof: (data: { fileData: string; fileName: string; employeeNo: string }) =>
    api.post('/files/identity-proof', data),

  deleteFile: (fileId: string, driveId: string) =>
    api.delete(`/files/${fileId}`, { data: { driveId } }),
};

export default api;
